/**
 * 下拉選項：把已接契約的 DcCustomer / DcRoom DTO 轉成表單用的 {id,value,label}。
 *
 * 顯示欄位對不上時 console.warn，不准安靜變空（ticket 16）。
 * 頁面查詢參數寫死在這裡，避免各頁各寫一個 pageSize、或靠後端 Int32.Max 預設。
 * dcCustomersOptions 的 pageSize 與 UsersPage 進階篩選同一把 queryKey，好共用快取。
 */

/** 下拉一次拿齊。Int32.Max — 契約裡「給我全部」的寫法。 */
export const FETCH_ALL_PAGE_SIZE = 2147483647;

/** 契約：customerRoleId 0＝不限。空字串／省略真後端 400 NotEmpty。 */
export const CUSTOMER_LOOKUP_PARAMS = {
  customerRoleId: 0,
  pageIndex: 0,
  pageSize: FETCH_ALL_PAGE_SIZE,
};
export const ROOM_LOOKUP_PARAMS = { pageIndex: 0, pageSize: FETCH_ALL_PAGE_SIZE };
export const ROLE_LOOKUP_PARAMS = { pageIndex: 0, pageSize: FETCH_ALL_PAGE_SIZE };

export function managerOptionFromCustomer(c) {
  if (!c) return null;
  if (!c.name) {
    console.warn(
      `[lookup] DcCustomer id=${c.id} 缺 name，下拉改顯示 id。對照 helpers/lookupOptions.js`
    );
  }
  if (!c.employee_no) {
    console.warn(
      `[lookup] DcCustomer id=${c.id} 缺 employee_no，下拉改顯示「無員編」。對照 helpers/lookupOptions.js`
    );
  }
  const name = c.name || `（id ${c.id}）`;
  const emp = c.employee_no || "無員編";
  return { id: c.id, value: String(c.id), label: `${name}（${emp}）` };
}

export function managerOptionsFromCustomers(items) {
  return (items || []).map(managerOptionFromCustomer).filter(Boolean);
}

/**
 * DcCustomer DTO → 工作項目／轉派／審核選人用的人員列。
 * 頁面仍讀 employeeId／department／title（舊 REST 形狀）;來源是契約欄位。
 */
export function customerPersonView(c) {
  if (!c) return null;
  if (!c.name) {
    console.warn(
      `[lookup] DcCustomer id=${c.id} 缺 name，列上改顯示 id。對照 helpers/lookupOptions.js`
    );
  }
  if (!c.employee_no) {
    console.warn(
      `[lookup] DcCustomer id=${c.id} 缺 employee_no，列上改顯示「無員編」。對照 helpers/lookupOptions.js`
    );
  }
  if (!c.unit_name) {
    console.warn(
      `[lookup] DcCustomer id=${c.id} 缺 unit_name，單位改顯示 —。對照 helpers/lookupOptions.js`
    );
  }
  if (!c.job_title) {
    console.warn(
      `[lookup] DcCustomer id=${c.id} 缺 job_title，職稱改顯示 —。對照 helpers/lookupOptions.js`
    );
  }
  return {
    ...c,
    id: c.id,
    name: c.name || `（id ${c.id}）`,
    employeeId: c.employee_no || "無員編",
    department: c.unit_name || "—",
    title: c.job_title || "—",
    roleId: c.role_id,
    account: c.username || "",
    email: c.email,
  };
}

/** 角色欄／審核篩選：契約 system_name。對不上時 warn，不准安靜變空。 */
export function roleCodeFromRoles(roleId, roles) {
  if (roleId === undefined || roleId === null || roleId === "") return "—";
  const r = (roles || []).find((x) => String(x.id) === String(roleId));
  if (!r) {
    console.warn(
      `[lookup] DcCustomerRole id=${roleId} 找不到，角色欄改顯示 id。對照 helpers/lookupOptions.js`
    );
    return `（id ${roleId}）`;
  }
  if (!r.system_name && !r.name) {
    console.warn(
      `[lookup] DcCustomerRole id=${r.id} 缺 system_name，角色欄改顯示 id。對照 helpers/lookupOptions.js`
    );
    return `（id ${r.id}）`;
  }
  if (!r.system_name) {
    console.warn(
      `[lookup] DcCustomerRole id=${r.id} 缺 system_name，改顯示 name。對照 helpers/lookupOptions.js`
    );
  }
  return r.system_name || r.name;
}

/**
 * 使用者列表角色欄：用 role_id 去 DcCustomerRole 查 name。
 * 真後端常把 role_name 填成 Registered（nop 預設顧客角色），跟 role_id 對不上。
 */
export function roleNameFromRoles(roleId, roles, fallbackName) {
  if (roleId === undefined || roleId === null || roleId === "") {
    return fallbackName || "—";
  }
  const r = (roles || []).find((x) => String(x.id) === String(roleId));
  if (!r) {
    console.warn(
      `[lookup] DcCustomerRole id=${roleId} 找不到，角色欄改顯示 role_name。對照 helpers/lookupOptions.js`
    );
    return fallbackName || `（id ${roleId}）`;
  }
  const label = r.name || r.system_name;
  if (!label) {
    console.warn(
      `[lookup] DcCustomerRole id=${r.id} 缺 name，角色欄改顯示 role_name／id。對照 helpers/lookupOptions.js`
    );
    return fallbackName || `（id ${r.id}）`;
  }
  if (fallbackName && fallbackName !== label) {
    console.warn(
      `[lookup] DcCustomer role_id=${roleId} 對應「${label}」，但 role_name 是「${fallbackName}」。以 id 查表為準。對照 helpers/lookupOptions.js`
    );
  }
  return label;
}

export function roomViewFromDto(dto, floors = []) {
  if (!dto) return null;
  if (!dto.name_chinese) {
    console.warn(
      `[lookup] DcRoom id=${dto.id} 缺 name_chinese，下拉改顯示 id。對照 helpers/lookupOptions.js`
    );
  }
  return {
    ...dto,
    name: dto.name_chinese || `（id ${dto.id}）`,
    floors,
  };
}

export function roomsViewFromDtos(items, structures = {}) {
  return (items || [])
    .map((r) => roomViewFromDto(r, structures[r.id] || []))
    .filter(Boolean);
}

/** 樓層／區域 id → 名稱（詳情頁；DTO 只有 id） */
export function floorAreaNames(rooms, floorId, areaId) {
  if (floorId == null || floorId === "") return { floorName: "", areaName: "" };
  for (const room of rooms || []) {
    for (const fl of room.floors || []) {
      if (String(fl.id) === String(floorId)) {
        const area = (fl.areas || []).find((a) => String(a.id) === String(areaId));
        return { floorName: fl.name || "", areaName: area ? area.name : "" };
      }
    }
  }
  return { floorName: "", areaName: "" };
}

/** 管理者下拉 label（姓名（員編））；找不到才用列表上的名字 */
export function managerDisplay(managers, managerId, fallback) {
  if (managerId == null || managerId === "" || Number(managerId) === 0) {
    return fallback || "—";
  }
  const hit = (managers || []).find((m) => String(m.value ?? m.id) === String(managerId));
  if (hit && hit.label) return hit.label;
  return fallback || "—";
}

/** 下拉資源載入失敗時給畫面的說明；沒失敗回空字串。 */
export function lookupLoadError(kind, error) {
  if (!error) return "";
  return `${kind}選項載入失敗，請稍後再試`;
}
